/**
 * usePush.js — хук управления Web Push подпиской
 *
 * Использование:
 *   const { isSupported, isSubscribed, subscribe, unsubscribe, sendTestPush } = usePush();
 *
 * subscribe() — запрашивает permission и регистрирует подписку на бэкенде.
 * Возвращает { ok } или { ok: false, reason, step, message } — текст ошибки
 * для тоста берётся через pushErrorKey(result).
 *
 * ⚠️ Android, установленное PWA (2026-10): кнопка висела на «Подключение…».
 * navigator.serviceWorker.ready ждёт АКТИВНОГО SW без таймаута, а SW при первой
 * установке прогревал кэш всей статики и активировался минутами. Теперь:
 *   — SW берём через getRegistration() и ждём активации с таймаутом;
 *   — pushManager.subscribe (поход в FCM) тоже под таймаутом;
 *   — уже существующая подписка переиспользуется, подписка со старым
 *     VAPID-ключом снимается (иначе subscribe падает InvalidStateError);
 *   — каждый провал уходит в PostHog: push_subscribe_failed { step, reason }.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/client';
import { track } from '../analytics';

const SW_READY_TIMEOUT_MS   = 20000;
const SUBSCRIBE_TIMEOUT_MS  = 20000;

// Конвертация base64url → Uint8Array (нужно для VAPID ключа)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

function withTimeout(promise, ms, code) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const e = new Error(code);
      e.code = code;
      reject(e);
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

// Подписка выдана под тем же VAPID-ключом? options.applicationServerKey —
// ArrayBuffer; у старых браузеров поля может не быть — тогда считаем «тот же».
function sameServerKey(subscription, keyBytes) {
  const raw = subscription?.options?.applicationServerKey;
  if (!raw) return true;
  const a = new Uint8Array(raw);
  if (a.length !== keyBytes.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== keyBytes[i]) return false;
  return true;
}

// Активная регистрация SW. В проде SW регистрируется после логина
// (utils/serviceWorker.js), но в свежеустановленном PWA он может быть ещё не
// зарегистрирован или ещё устанавливаться — тогда регистрируем сами и ждём
// активации ограниченное время. В деве SW намеренно снимается (index.js) —
// там только ждём.
async function getActiveRegistration() {
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg && process.env.NODE_ENV === 'production') {
    reg = await navigator.serviceWorker.register('/sw.js');
  }
  if (reg?.active) return reg;
  return withTimeout(navigator.serviceWorker.ready, SW_READY_TIMEOUT_MS, 'sw_not_ready');
}

async function saveSubscription(subscription) {
  const subJson = subscription.toJSON();
  await api.pushSubscribe({
    endpoint:  subJson.endpoint,
    keys:      subJson.keys,
    userAgent: navigator.userAgent,
  });
}

// Ключ i18n для тоста по результату subscribe(). null — тост не нужен.
export function pushErrorKey(result) {
  if (!result || result.ok || result.reason === 'loading') return null;
  switch (result.reason) {
    case 'permission_denied': return 'settings.pushPermissionDenied';
    case 'sw_not_ready':      return 'settings.pushErrSwNotReady';
    case 'subscribe_timeout': return 'settings.pushErrTimeout';
    case 'not_supported':     return 'settings.pushNotSupported';
    default:                  return 'settings.pushErrGeneric';
  }
}

export function usePush() {
  const [isSupported, setIsSupported]   = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission]     = useState('default'); // 'default' | 'granted' | 'denied'
  const [loading, setLoading]           = useState(false);
  const [vapidKey, setVapidKey]         = useState(null);
  const busyRef = useRef(false);

  // Проверяем поддержку и текущее состояние подписки при монтировании
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    if (!supported) return;

    setPermission(Notification.permission);

    // Проверяем есть ли активная подписка
    navigator.serviceWorker.ready.then(reg => {
      reg.pushManager.getSubscription().then(sub => {
        setIsSubscribed(!!sub);
      });
    }).catch(() => {});

    // Загружаем VAPID ключ заранее — чтобы при tap не было async-разрыва (важно для iOS Safari)
    api.getPushVapidKey().then(({ publicKey }) => setVapidKey(publicKey)).catch(() => {});
  }, []);

  // Подписаться на push-уведомления
  const subscribe = useCallback(async () => {
    if (!isSupported) return { ok: false, reason: 'not_supported' };
    if (busyRef.current) return { ok: false, reason: 'loading' };
    busyRef.current = true;
    setLoading(true);
    const startedAt = Date.now();
    let step = 'vapid';

    try {
      // Используем предзагруженный ключ; если вдруг нет — грузим синхронно
      let publicKey = vapidKey;
      if (!publicKey) {
        const res = await api.getPushVapidKey();
        publicKey = res.publicKey;
      }

      // Запрашиваем permission у пользователя
      step = 'permission';
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        track('push_subscribe_failed', { step, reason: 'permission_denied', permission: perm });
        return { ok: false, reason: 'permission_denied', step };
      }

      step = 'service_worker';
      const reg = await getActiveRegistration();

      step = 'subscribe';
      const keyBytes = urlBase64ToUint8Array(publicKey);
      let subscription = await reg.pushManager.getSubscription();
      if (subscription && !sameServerKey(subscription, keyBytes)) {
        await subscription.unsubscribe().catch(() => {});
        subscription = null;
      }
      if (!subscription) {
        subscription = await withTimeout(
          reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes }),
          SUBSCRIBE_TIMEOUT_MS,
          'subscribe_timeout'
        );
      }

      // Сохраняем на бэкенде
      step = 'save';
      await saveSubscription(subscription);

      setIsSubscribed(true);
      track('push_subscribe_success', { duration_ms: Date.now() - startedAt });
      return { ok: true };

    } catch (e) {
      const reason = e?.code && typeof e.code === 'string' ? e.code : (e?.name || 'unknown');
      const message = String(e?.message || '').slice(0, 200);
      console.error('[usePush] subscribe error:', step, e);
      track('push_subscribe_failed', { step, reason, message, duration_ms: Date.now() - startedAt });
      return { ok: false, reason, step, message };
    } finally {
      busyRef.current = false;
      setLoading(false);
    }
  }, [isSupported, vapidKey]);

  // Отписаться
  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;
    setLoading(true);
    try {
      const reg = await getActiveRegistration();
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.pushUnsubscribe({ endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
    } catch (e) {
      console.error('[usePush] unsubscribe error:', e);
    } finally {
      setLoading(false);
    }
  }, [isSupported]);

  // Тестовый push на ЭТО устройство.
  // Перед тестом пересохраняем текущую подписку (upsert) — лечит случай, когда
  // браузер подписан, а бэкенд о подписке не знает (подписка дошла до FCM
  // уже после того, как юзер ушёл с экрана). Бэкенд возвращает итог по
  // endpoint'у этого устройства: { this_device: { found, ok, status, error } }.
  const sendTestPush = useCallback(async () => {
    try {
      let endpoint = null;
      if (isSupported) {
        const reg = await getActiveRegistration().catch(() => null);
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (sub) {
          endpoint = sub.endpoint;
          await saveSubscription(sub).catch(() => {});
        }
      }
      const res = await api.pushTest({ endpoint });
      const d = res?.this_device || {};
      track('push_test_result', {
        has_browser_subscription: !!endpoint,
        found: !!d.found, ok: !!d.ok, status: d.status || null,
        sent: res?.sent ?? null, total: res?.total ?? null,
      });
      return { ok: true, ...res, endpoint };
    } catch (e) {
      track('push_test_result', { error: String(e?.message || '').slice(0, 200) });
      return { ok: false, reason: e.message };
    }
  }, [isSupported]);

  return { isSupported, isSubscribed, permission, loading, subscribe, unsubscribe, sendTestPush };
}
